import { Avatar, Icon, Loading, useToast } from "@uzverse/ui";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorNote, useCommonT, useErrorText } from "../components/common.js";
import { AuthHeader } from "../components/Layout.js";
import { useAssistant } from "../state/assistant.js";
import { useSession } from "../state/session.js";
import { playPreview } from "../voice/preview.js";
import { voicesForLanguage } from "./SettingsPage.js";

/** First run after an invite: name the assistant, then pick its voice. */
export function OnboardingPage() {
  const { t, i18n } = useTranslation("web");
  const tc = useCommonT();
  const errorText = useErrorText();
  const toast = useToast();
  const navigate = useNavigate();
  const { services } = useSession();
  const { settings, catalog, save } = useAssistant();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState("");
  const [voiceKey, setVoiceKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);

  useEffect(() => {
    if (settings && !name) setName(settings.name);
    // Only seed once, when the settings arrive.
  }, [settings]);

  const voices = catalog ? voicesForLanguage(catalog, i18n.language) : [];

  async function finish(skipVoice: boolean) {
    setBusy(true);
    setError(null);
    try {
      const [providerId, voiceId] = voiceKey.split("::");
      await save({
        name: name.trim(),
        ...(!skipVoice && providerId && voiceId ? { voice: { providerId, voiceId } } : {}),
      });
      void navigate("/", { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function next(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) setStep(2);
  }

  async function preview(key: string) {
    const [providerId, voiceId] = key.split("::");
    if (!providerId || !voiceId) return;
    setPreviewing(key);
    try {
      playPreview(await services.api.previewVoice(providerId, voiceId));
    } catch (err) {
      toast.show(errorText(err), "error");
    } finally {
      setPreviewing(null);
    }
  }

  return (
    <main className="auth">
      <div className="auth-card card onboarding">
        <AuthHeader />
        <ol className="steps" aria-label={t("onboarding.step", { n: step, total: 2 })}>
          <li className={step >= 1 ? "done" : ""} aria-current={step === 1 ? "step" : undefined} />
          <li className={step >= 2 ? "done" : ""} aria-current={step === 2 ? "step" : undefined} />
        </ol>
        <div className="center-col">
          <Avatar name={name || "?"} assistant size={56} />
          <h1>{t("onboarding.title")}</h1>
          <p className="muted">{t("onboarding.intro")}</p>
        </div>
        {!settings ? (
          <Loading t={tc} />
        ) : step === 1 ? (
          <form className="stack" onSubmit={next}>
            <label className="field">
              <span>{t("onboarding.nameTitle")}</span>
              <input
                value={name}
                maxLength={40}
                required
                autoFocus
                onChange={(e) => setName(e.target.value)}
              />
              <small className="muted">{t("onboarding.nameHint")}</small>
            </label>
            <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
              {t("onboarding.next")}
            </button>
          </form>
        ) : (
          <div className="stack">
            <div>
              <h2>{t("onboarding.voiceTitle")}</h2>
              <p className="muted small">{t("onboarding.voiceHint")}</p>
            </div>
            {voices.length === 0 ? (
              <p className="note">{t("onboarding.noVoices")}</p>
            ) : (
              <ul
                className="plain voice-list"
                role="radiogroup"
                aria-label={t("onboarding.voiceTitle")}
              >
                {voices.map((v) => {
                  const key = `${v.providerId}::${v.id}`;
                  const provider = catalog?.providers.find((p) => p.id === v.providerId);
                  return (
                    <li key={key} className={`voice-item${voiceKey === key ? " is-selected" : ""}`}>
                      <label className="voice-pick">
                        <input
                          type="radio"
                          name="voice"
                          checked={voiceKey === key}
                          onChange={() => setVoiceKey(key)}
                        />
                        <span className="grow">
                          {v.name}
                          <small className="muted block">
                            {v.paid ? t("settings.voice.paid") : t("settings.voice.free")}
                          </small>
                        </span>
                      </label>
                      {provider?.capabilities["preview"] === true && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={previewing !== null}
                          onClick={() => void preview(key)}
                        >
                          <Icon name="volume" size={14} />
                          {previewing === key
                            ? t("settings.voice.previewing")
                            : t("settings.voice.preview")}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <ErrorNote message={error} />
            <div className="row row-between">
              <button type="button" className="btn btn-quiet" onClick={() => setStep(1)}>
                {t("onboarding.back")}
              </button>
              <span className="row">
                <button
                  type="button"
                  className="btn btn-quiet"
                  disabled={busy}
                  onClick={() => void finish(true)}
                >
                  {t("onboarding.skip")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void finish(false)}
                >
                  {t("onboarding.finish")}
                </button>
              </span>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
